/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, IconButton, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { languageOptions } from '../../../reducers/translation';
import { DocumentTranslationOptionDtoTargetLanguageEnum } from '@ink-ai/insight-service-sdk';

interface LanguageTabsProps {
  languages: DocumentTranslationOptionDtoTargetLanguageEnum[];
  selectedLanguage: DocumentTranslationOptionDtoTargetLanguageEnum;
  setSelectedLanguage: (
    language: DocumentTranslationOptionDtoTargetLanguageEnum,
  ) => void;
  anchorEl: null | HTMLElement;
  setAnchorEl: (element: null | HTMLElement) => void;
  disabled?: boolean;
}

export const LanguageTabs: React.FC<LanguageTabsProps> = ({
  languages,
  selectedLanguage,
  setSelectedLanguage,
  anchorEl,
  setAnchorEl,
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tabs
        value={selectedLanguage}
        onChange={(_, newValue) => {
          if (!disabled) {
            setSelectedLanguage(newValue);
          }
        }}
        sx={{ minWidth: 100 }}
      >
        {languages.map((language) => (
          <Tab
            key={language}
            label={t(languageOptions.get(language))}
            value={language}
            sx={{ textTransform: 'none' }}
          />
        ))}
      </Tabs>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
      >
        <ArrowDropDownIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {Array.from(languageOptions.keys())
          .filter((language) => !languages.includes(language))
          .map((language) => (
            <MenuItem
              key={language}
              onClick={() => {
                if (!disabled) {
                  setSelectedLanguage(language);
                  setAnchorEl(null);
                }
              }}
            >
              {t(languageOptions.get(language))}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};
